import { cloneDeep } from "lodash-es";
import { assignDeep } from "@/helpers/utility/CollectionFunctions";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSimulationStore } from "../simulation/simulations.store";

import { acceptHotItemStore, defineItemStore } from "../itemStore.store";
import { addItems, addSimulation, addSimulationSymbol, clearItemsOfType, removeItems, removeSimulations, updateAxis, updateChartType, updateRoundsToSimulations } from "../charts/chartStoreFunctions";

import type {
  ChartSettings,
  ChartType,
  ItemConfig,
  ConfigurableType,
  SelectableChartItem,
  SelectableChartItemType
} from "@editor/items/charts/ChartItem";
import type {
  EvaluationItem
} from "./EvaluationItem";
import type { Subset } from "@/helpers/utility/Type";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import type { Uid } from "@/helpers/utility/Id";

export const defaultDataToggles: EvaluationItem["dataToggles"] = {
  all: {
    target: false,
    resource: false,
    index: false,
    simulation: false,
    simulationRound: true
  }
};
export const defaultData: EvaluationItem["data"] = {
  simulation: [],
  simulationRound: [],
  target: [],
  resource: [],
  index: [],
  actionActivation: [],
  incidentActivation: [],
  scheduledEffectActivation: []
};
export const defaultItemConfigs: EvaluationItem["itemConfigs"] = {
  target: {},
  resource: {},
  index: {},
  simulation: {},
  actionActivation: {},
  incidentActivation: {},
  scheduledEffectActivation: {},
  simulationRound: {}
};
export const defaultChartSettings: EvaluationItem["chartSettings"] = {
  zoomed: false,
  width: null,
  height: null,
  scaleColorsVisible: true,
  ticksVisible: true,
  xAxis: "simulationRound",
  yAxis: "value",
  xAxisLabelsShortened: true,
  xAxisLabelsVisible: true,
  xAxisTitleVisible: true,
  yAxisLabelsShortened: true,
  yAxisLabelsVisible: true,
  yAxisTitleVisible: true,
  groupLabelsVisible: true,
  groupLabelsShortened: true,
  filterZeroValues: false,
  subValuesVisible: false,
  endPointsVisible: true,
  roundEffectsVisible: true,
  centered: false,
  areasColored: false,
  areasFilled: true,
  fontSize: 12,
  backgroundFilled: true,
  titleVisible: true
};

export const evaluationItemStructure: EvaluationItem = Object.freeze({
  type: "evaluation",
  title: "",
  titleIsCustom: false,
  chartType: null,
  isDefaultSimulation: false,

  data: defaultData,
  dataToggles: defaultDataToggles,
  itemConfigs: defaultItemConfigs,
  chartSettings: defaultChartSettings,

  id: 0,
  uid: "evaluation_0",
  createdAt: -1
});

export const useEvaluationStore = defineItemStore(
  "model/evaluations",
  evaluationItemStructure,
  {
    state: (baseState) => {
      // create default evaluation:
      baseState.count += 1;
      baseState.items[0] = {
        ...cloneDeep(evaluationItemStructure),
        id: 0,
        uid: "evaluation_0",
        isDefaultSimulation: true,
        title: "",
        createdAt: Date.now()
      };
      baseState.order = [0];

      return {
        titleAbbreviations: {} as { [uid: string]: string },

        imageExportSettings: {
          titleVisible: true,
          legendVisible: true,
          legendPosition: "left" as "left" | "right",
          imageFormat: "image/svg" as "image/png" | "image/svg"
        },

        lastUpdatedItemId: 0
      };
    },
    getters: {
      simulationEvaluationDefaults(): Partial<EvaluationItem> {
        return {
          chartType: "bar"
        };
      },

      unnamedEvaluations(): EvaluationItem[] {
        return this.orderedItems.filter((ev) => !ev.title);
      }
    },
    events: {
      async beforeCreate(evaluationItem) {
        const changes: Subset<EvaluationItem> = {};

        // Copy the item configuration of the last edited evaluation
        const lastEvaluation =
          this.items[this.lastUpdatedItemId] || this.items[0];
        if (lastEvaluation) {
          const itemConfigs = evaluationItem.itemConfigs || {};
          const extendedItemConfig = assignDeep(
            cloneDeep(lastEvaluation.itemConfigs),
            itemConfigs
          );
          changes.itemConfigs = extendedItemConfig;
        }

        // make sure all simulations used in the evaluation have a symbol
        // assigned
        let simulationIds = (evaluationItem.data?.simulation || []) as number[];
        if (evaluationItem.dataToggles?.all?.simulation) {
          simulationIds = [...useSimulationStore().ids];
        }
        for (const simulationId of simulationIds) {
          addSimulationSymbol(
            changes?.itemConfigs?.simulation || {},
            simulationId
          );
        }

        // make sure that evaluation items contain the currently open
        // simulation id as default
        const openSimulationId = useSimulationViewStore().openSimulationId;
        if (openSimulationId != null && evaluationItem?.data == null) {
          changes.data = cloneDeep(evaluationItemStructure.data);
          changes.data.simulation = [openSimulationId];
          changes.data.selectedSimulationId = openSimulationId;
        }

        return { ...evaluationItem, ...changes };
      },

      afterUpdate(evaluationItem) {
        this.lastUpdatedItemId = evaluationItem.id;
      }
    },
    actions: {
      updateChartSettings(evaluationId: number, data: Partial<ChartSettings>): void {
        const evaluation = this.items[evaluationId];
        const chartSettings = evaluation.chartSettings;
        Object.assign(chartSettings, data);

        this.lastUpdatedItemId = evaluationId;
      },

      toggleAllOption(
        evaluationId: number,
        type: "simulation" | "target" | "resource" | "simulationRound" | "index"
      ): boolean {
        const evaluation = this.items[evaluationId];
        const value = !evaluation.dataToggles.all[type];
        evaluation.dataToggles.all[type] = value;

        if (type === "simulation") {
          if (value) {
            // add symbols to all added simulations
            Object.keys(evaluation.itemConfigs.simulation).forEach(
              (simulationId) => {
                this.addSimulation(evaluationId, +simulationId);
              }
            );
          }
        }

        return value;
      },

      addSimulation(evaluationId: number, simulationId: number): void {
        const evaluation = this.items[evaluationId];
        addSimulation(evaluation, simulationId);
      },
      removeSimulations(evaluationId: number, simulationIds: number[]): void {
        const evaluation = this.items[evaluationId];
        removeSimulations(evaluation, simulationIds);
      },
      clearSimulations(evaluationId: number): void {
        const evaluation = this.items[evaluationId];
        evaluation.dataToggles.all.simulation = false;
        evaluation.data.simulation = [];

        updateRoundsToSimulations(evaluation);
      },
      updateAllSimulationIcons(evaluationId: number): void {
        const evaluation = this.items[evaluationId];
        // make sure all simulations used in the evaluation have a symbol
        // assigned
        let simulationIds = evaluation.data.simulation;
        if (evaluation.dataToggles.all.simulation) {
          simulationIds = [...useSimulationStore().ids];
        }
        for (const simulationId of simulationIds) {
          addSimulationSymbol(evaluation.itemConfigs.simulation, simulationId);
        }
      },

      addItems(
        chartId: number,
        itemIds: Uid<"target" | "resource" | "index">[]
      ): void {
        const chart = this.items[chartId];
        addItems(chart, itemIds);
      },
      removeItems(
        chartId: number,
        itemIds: Uid<"target" | "resource" | "index">[]
      ): void {
        const chart = this.items[chartId];
        removeItems(chart, itemIds);
      },
      clearItemsOfType(
        chartId: number,
        type: "target" | "resource" | "index"
      ): void {
        const chart = this.items[chartId];
        clearItemsOfType(chart, type);
      },

      addRounds(evaluationId: number, roundIndices: number[]): void {
        const evaluation = this.items[evaluationId];
        const evaluationData = evaluation.data;

        evaluation.dataToggles.all.simulationRound = false;

        evaluationData.simulationRound.push(...roundIndices);
      },
      removeRounds(evaluationId: number, roundIndices: number[]): void {
        const evaluation = this.items[evaluationId];

        evaluation.dataToggles.all.simulationRound = false;

        const ids = evaluation.data.simulationRound;
        for (const roundIndex of roundIndices) {
          const index = ids.indexOf(roundIndex);
          if (index < 0) continue;
          ids.splice(index, 1);
        }
      },
      setRounds(
        evaluationId: number,
        roundIndices: number[],
        options = { all: false }
      ): void {
        const evaluation = this.items[evaluationId];
        const evaluationData = evaluation.data;

        evaluation.dataToggles.all.simulationRound = options.all;
        evaluationData.simulationRound = [];
        evaluationData.simulationRound.push(...roundIndices);
      },

      addActionActivation(evaluationId: number, actionActivationId: string): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.actionActivation;

        if (ids.includes(actionActivationId)) return;

        ids.push(actionActivationId);
      },
      removeActionActivation(
        evaluationId: number,
        actionActivationId: string
      ): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.actionActivation;

        const index = ids.indexOf(actionActivationId);
        if (index == -1) return;

        ids.splice(index, 1);
      },

      addIncidentActivation(
        evaluationId: number,
        incidentActivationId: string
      ): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.incidentActivation;

        if (ids.includes(incidentActivationId)) return;

        ids.push(incidentActivationId);
      },
      removeIncidentActivation(
        evaluationId: number,
        incidentActivationId: string
      ): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.incidentActivation;

        const index = ids.indexOf(incidentActivationId);
        if (index == -1) return;

        ids.splice(index, 1);
      },

      addScheduledEffectActivation(evaluationId: number, activationId: string): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.scheduledEffectActivation;

        if (ids.includes(activationId)) return;

        ids.push(activationId);
      },
      removeScheduledEffectActivation(
        evaluationId: number,
        activationId: string
      ): void {
        const evaluation = this.items[evaluationId];

        const ids = evaluation.data.scheduledEffectActivation;

        const index = ids.indexOf(activationId);
        if (index == -1) return;

        ids.splice(index, 1);
      },

      setAxis(
        evaluationId: number,
        dimension: "x" | "y" | null,
        value: string
      ): void {
        if (dimension == null) return;

        const evaluation = this.items[evaluationId];
        updateAxis(evaluation, dimension, value);
      },

      setChartType(evaluationId: number, chartType: ChartType): void {
        const evaluation = this.items[evaluationId];

        updateChartType(evaluation, chartType);
      },

      updateItemOrder<T extends SelectableChartItemType>(
        evaluationId: number,
        itemType: T,
        order: SelectableChartItem<T>["id"][]
      ): void {
        const evaluation = this.items[evaluationId];

        evaluation.data[itemType] =
          order.slice() as (typeof evaluation.data)[T];
      },

      selectItem(evaluationId: number, item: SelectableChartItem | null): void {
        const evaluation = this.items[evaluationId];

        evaluation.data.selectedItem = item
          ? ({ id: item.id, type: item.type } as SelectableChartItem)
          : undefined;
      },

      /**
       * Select the main simulation of an evaluation.
       */
      selectSimulation(evaluationId: number, simulationId?: number | null): void {
        const evaluation = this.items[evaluationId];
        evaluation.data.selectedSimulationId = simulationId ?? undefined;

        console.log("selected simulation", simulationId);

        // make sure the rounds are not out of bounds:
        updateRoundsToSimulations(evaluation);
      },

      updateRoundsToSimulations(evaluationId: number): void {
        const evaluation = this.items[evaluationId];
        updateRoundsToSimulations(evaluation);
      },

      // ==== ITEM CONFIGURATION ====

      /**
       * Get an item configuration, making sure it exists.
       * @param evaluationId
       * @param itemType
       * @param itemId
       */
      getOrCreateItemConfig(
        evaluationId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup?: string
      ): ItemConfig {
        const evaluation = this.items[evaluationId];

        if (!(itemType in evaluation.itemConfigs)) {
          evaluation.itemConfigs[itemType] = {};
        }
        if (!(itemId in evaluation.itemConfigs[itemType])) {
          evaluation.itemConfigs[itemType][itemId] = {
            open: false
          };
        }
        const itemConfig = evaluation.itemConfigs[itemType][itemId];
        if (!subgroup) return itemConfig;

        if (!itemConfig.subgroups) itemConfig.subgroups = {};
        if (!itemConfig.subgroups[subgroup]) {
          itemConfig.subgroups[subgroup] = {
            open: false
          };
        }
        return itemConfig.subgroups[subgroup];
      },

      updateItemConfig(
        evaluationId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup: string | undefined,
        changes: object = {}
      ): void {
        const config = this.getOrCreateItemConfig(
          evaluationId,
          itemType,
          itemId,
          subgroup
        );
        Object.assign(config, changes);
      },

      setItemSymbol(
        evaluationId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        symbol: UnfilledSymbolName,
        subgroup?: string
      ): void {
        const config = this.getOrCreateItemConfig(
          evaluationId,
          itemType,
          itemId,
          subgroup
        );
        config.symbol = symbol;

        this.lastUpdatedItemId = evaluationId;
      },

      setItemColor(
        evaluationId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        color: string | null,
        simulationId?: number,
        subgroup?: string
      ): void {
        const config = this.getOrCreateItemConfig(
          evaluationId,
          itemType,
          itemId,
          subgroup
        );
        if (simulationId != null) {
          if (!(simulationId in config)) config[simulationId] = {};
          config[simulationId].color = color;
        } else {
          config.color = color;
        }

        this.lastUpdatedItemId = evaluationId;
      },

      closeItemConfig(
        evaluationId: number,
        itemType: ConfigurableType,
        itemId: number | string,
        subgroup: string | undefined,
        options: { color?: string | null } = {}
      ): void {
        const config = this.getOrCreateItemConfig(
          evaluationId,
          itemType,
          itemId,
          subgroup
        );
        Object.assign(config, options);
        config.open = false;

        // remove all simulation-specific configs
        for (const prop in config) {
          if (!isNaN(parseInt(prop))) {
            delete config[prop];
          }
        }

        this.lastUpdatedItemId = evaluationId;
      },

      updateTitleAbbreviation(uid: string, value: string): void {
        this.titleAbbreviations[uid] = value;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useEvaluationStore, import.meta.hot)
  );
}
